"use client";

/**
 * MIN-293 — `AgentChat.Turn`: one assistant turn, laid out as shadcn's Chat
 * and Message patterns lay out a reply. Each part `kind` renders through the
 * merged `PartRenderers` registry reached off context (`meta.renderers`), so
 * an app overriding one kind still gets this layout around it:
 *
 *   Trace (reasoning + tool steps) → answer → opaque parts → Stopped →
 *   Sources → footer actions.
 *
 * Citations and `outcome` render once per turn, not once per part. The
 * "Stopped" marker derives from `turn.outcome`, which can't read "streaming"
 * and "interrupted" at once, so it never shows mid-stream.
 */
import { Button } from "@repo/ui/components/ui/button";
import { Marker, MarkerContent, MarkerIcon } from "@repo/ui/components/ui/marker";
import { Message, MessageContent, MessageFooter } from "@repo/ui/components/ui/message";
import { Spinner } from "@repo/ui/components/ui/spinner";
import { Tooltip, TooltipContent, TooltipTrigger } from "@repo/ui/components/ui/tooltip";
import {
  Trace,
  TraceCollapsible,
  TraceCollapsibleContent,
  TraceCollapsibleTrigger,
  TraceIcon,
  TraceLine,
  TraceTitle,
} from "@repo/ui/components/ui/trace";
import { CheckIcon, CopyIcon } from "lucide-react";
import { type ComponentProps, Fragment, useEffect, useState } from "react";
import { StatusIcon } from "../elements/trace-step";
import type { ToolPart, Turn as TurnModel, TurnPart } from "../turn";
import { useAgentChatContext } from "./context";

type ReasoningPart = Extract<TurnPart, { kind: "reasoning" }>;
type TextPart = Extract<TurnPart, { kind: "text" }>;
type CitationPart = Extract<TurnPart, { kind: "citation" }>;
type OpaquePart = Extract<TurnPart, { kind: "opaque" }>;

function MessageAction({ label, children, ...props }: ComponentProps<typeof Button> & { label: string }) {
  return (
    <Tooltip>
      <TooltipTrigger render={<Button variant="ghost" size="icon-sm" aria-label={label} {...props} />}>
        {children}
      </TooltipTrigger>
      <TooltipContent>
        <p>{label}</p>
      </TooltipContent>
    </Tooltip>
  );
}

function CopyAction({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timeout = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timeout);
  }, [copied]);

  return (
    <MessageAction
      label={copied ? "Copied" : "Copy"}
      onClick={() => {
        void navigator.clipboard.writeText(text);
        setCopied(true);
      }}
    >
      {copied ? <CheckIcon /> : <CopyIcon />}
    </MessageAction>
  );
}

/** Live status while the model works with nothing else on screen to say so —
 * the shimmer docs' Marker pattern. */
export function WorkingMarker({ label }: { label: string }) {
  return (
    <Marker role="status">
      <MarkerIcon>
        <Spinner />
      </MarkerIcon>
      <MarkerContent className="shimmer">{label}</MarkerContent>
    </Marker>
  );
}

/** shadcn's Chain of Thought pattern: the turn's tool steps under one
 * collapsible summary, open while the agent researches. */
function ChainOfThought({ turn, steps, answering }: { turn: TurnModel; steps: ToolPart[]; answering: boolean }) {
  const { meta } = useAgentChatContext();
  const [open, setOpen] = useState<boolean>();
  const running = turn.outcome === "streaming" && !answering;
  const failed = steps.filter((part) => part.state === "error").length;
  const status = running ? "running" : failed > 0 ? "error" : undefined;

  return (
    <TraceCollapsible open={open ?? running} onOpenChange={setOpen}>
      <TraceCollapsibleTrigger>
        <TraceLine status={status}>
          <TraceIcon>
            <StatusIcon status={status} />
          </TraceIcon>
          <TraceTitle>
            {running
              ? "Researching"
              : `Researched in ${steps.length} ${steps.length === 1 ? "step" : "steps"}${failed > 0 ? `, ${failed} failed` : ""}`}
          </TraceTitle>
        </TraceLine>
      </TraceCollapsibleTrigger>
      <TraceCollapsibleContent keepMounted>
        <Trace>
          {steps.map((part) => (
            <Fragment key={part.callId}>{meta.renderers.tool({ part, turn })}</Fragment>
          ))}
        </Trace>
      </TraceCollapsibleContent>
    </TraceCollapsible>
  );
}

export function AgentChatTurn({ turn }: { turn: TurnModel }) {
  const { meta } = useAgentChatContext();
  const renderers = meta.renderers;

  const reasoningPart = turn.parts.find((p): p is ReasoningPart => p.kind === "reasoning");
  const toolParts = turn.parts.filter((p): p is ToolPart => p.kind === "tool");
  const textPart = turn.parts.find((p): p is TextPart => p.kind === "text");
  const citationParts = turn.parts.filter((p): p is CitationPart => p.kind === "citation");
  const opaqueParts = turn.parts.filter((p): p is OpaquePart => p.kind === "opaque");

  const isStreaming = turn.outcome === "streaming";
  const hasRunningTool = toolParts.some((t) => t.state === "running");
  const reply = textPart?.text ?? "";
  // Streaming, no text yet, no tool running, no reasoning shown — never a blank turn.
  const working = isStreaming && !reply && !hasRunningTool && !reasoningPart;
  const hasSteps = !!reasoningPart || toolParts.length > 0;

  return (
    <Message>
      <MessageContent className="gap-3">
        {hasSteps && (
          <Trace>
            {reasoningPart && renderers.reasoning({ part: reasoningPart, turn })}
            {toolParts.length > 0 && <ChainOfThought turn={turn} steps={toolParts} answering={reply.length > 0} />}
          </Trace>
        )}
        {textPart
          ? renderers.text({ part: textPart, turn })
          : working && <WorkingMarker label={toolParts.length > 0 ? "Composing answer…" : "Thinking…"} />}
        {opaqueParts.map((part, index) => (
          // No id of its own; a turn's opaque parts don't reorder in place.
          <Fragment key={`${part.renderAs}-${index}`}>{renderers.opaque({ part, turn })}</Fragment>
        ))}
        {renderers.outcome({ turn })}
        {citationParts.length > 0 && renderers.citation({ parts: citationParts, turn })}
        {!isStreaming && reply && (
          <MessageFooter className="px-0">
            <CopyAction text={reply} />
          </MessageFooter>
        )}
      </MessageContent>
    </Message>
  );
}
